/**
 * Product/deployment configuration keys owned by the **platform shell** (见 05).
 *
 * 见 05 recorded the gap this closes: `workbench.startupEditorView` was supplied
 * straight from the override layer with only consumer-side validation, and the
 * note said that once shell keys multiplied there should be a platform-owned
 * schema/default layer rather than hosting them in some business module's
 * manifest. The rail preset keys below are that moment.
 *
 * These participate in `listProductConfig()` alongside module-declared defaults.
 * They are consulted **before** module declarations so a module cannot shadow a
 * shell key by declaring the same one.
 */
import type { ProductConfigContribution } from './index';
import { NETWORK_PROXY_KEY } from './network-proxy';
import { DEFAULT_UPDATE_CHECK_INTERVAL_MINUTES } from './update-contract';

/** Editor view opened on first workbench mount (见 05 工作台启动视图). */
export const STARTUP_EDITOR_VIEW_KEY = 'workbench.startupEditorView';

/**
 * Ordered rail preset per section, and the un-unpinnable set (见 21).
 *
 * This is the deployment preset layer: the initial arrangement, overridable by
 * the user at runtime. `locked` is the one thing a preset can force — see
 * `RailPresetLayer` for why the priority direction is the opposite of the
 * configuration system's.
 *
 * Values are plain arrays. The resolution layer has always carried `unknown`
 * (`bundle.allowedOrigins` in the base `product.json` is already an array); the
 * `array` member of `ProductConfigType` just lets the declaration say so.
 */
export const RAIL_TOP_KEY = 'workbench.rail.top';
export const RAIL_MIDDLE_KEY = 'workbench.rail.middle';
export const RAIL_BOTTOM_KEY = 'workbench.rail.bottom';
export const RAIL_LOCKED_KEY = 'workbench.rail.locked';

/**
 * Launch item keys the tray menu offers to open (需求 §4.6.2「打开 HiBro / 打开 AI」).
 *
 * Same value space as `workbench.rail.*` — a `LaunchItem.key` — and for the same
 * reason: what「打开 AI」means is already settled by whoever contributed that
 * launchable, and the tray should not become a second definition of it. So the
 * deployment lists keys here, the host projects them, and clicking one runs the
 * ordinary `workbench.open` command. The platform never learns that one of these
 * happens to be an AI module.
 *
 * Empty leaves the menu with just「打开 HiBro」, which is the whole tray menu a
 * deployment with no such entry point should have.
 */
export const TRAY_ITEMS_KEY = 'workbench.tray.items';

/**
 * Where the desktop looks for new builds, and how often (见 25).
 *
 * Deployment facts rather than user settings: which feed a build follows is
 * decided by the channel it was published under, so the values live in
 * `product.<channel>.json` next to the backend base URLs. An empty `feedUrl`
 * is the honest way to say "this build does not self-update" — the driver then
 * reports `canCheck: false` and the settings row says so, instead of polling an
 * address nobody is serving.
 */
export const UPDATE_FEED_URL_KEY = 'update.feedUrl';
export const UPDATE_CHECK_INTERVAL_KEY = 'update.checkIntervalMinutes';
/** Page a user is sent to when this runtime cannot install by itself (见 25). */
export const UPDATE_DOWNLOAD_PAGE_KEY = 'update.downloadPageUrl';

/**
 * Which module may hand inbound pushes to the platform (见 26).
 *
 * The transport is a module's (it dials a gateway with account credentials and
 * knows the SDK), the routing is the platform's (`matchPushOwner` +
 * `onPush:<moduleId>`). This key is the join: exactly one module id, pinned by
 * the deployment, may call `context.deliverPlatformPush`.
 *
 * A product-config key rather than a constant in platform code, for the reason
 * every other module id is absent from `packages/**`: the platform naming one
 * business module hard-codes a dependency it has no way to justify. Empty means
 * this deployment has no push provider — the same deliberate configuration an
 * empty gateway used to state, and every caller is then refused.
 *
 * Not a permission a module declares about itself: "I am the push provider" is
 * precisely the claim that must come from outside the module.
 *
 * It sits in the platform's own `platform.` namespace rather than under `push.`
 * for the same reason: that prefix belongs to the provider module — `modules/push`
 * declares `push.gatewayUrl` / `push.apiBaseUrl` there — so a shell key inside it
 * reads as one more setting of the very module it is supposed to constrain from
 * outside. It is therefore the **only** push key declared here; the addresses
 * live in `platform-push.ts` next to the rest of that axis's contract.
 */
export const PUSH_PROVIDER_KEY = 'platform.pushProvider';

/**
 * Deployment-owned public-entry rewrites applied before structural deep-link
 * resolution (见 20 公开入口别名).
 *
 * The value is an array of `{ from, to }` logical path prefixes. Keeping it in
 * product policy rather than a module manifest prevents downloaded modules from
 * claiming a public route that may already be printed in QR codes or messages.
 */
export const LINK_ALIASES_KEY = 'platform.linkAliases';

/**
 * Deployment-owned external address ownership (见 20 外链归属路由).
 *
 * Value is an array of `{ prefix, moduleId }`. It lets a deployment point one of
 * its own external addresses at a module **without touching that module's
 * manifest** — the case that matters when the module ships separately from the
 * deployment. Installed modules may also declare `contributes.urlOwnership`
 * themselves (see `listUrlOwnership`); which prefixes a store package may claim
 * is a review decision, not a runtime one.
 *
 * The deployment side is what can be audited: it writes down "this origin
 * belongs to that module" instead of the module asserting it about itself.
 * `prefix` is validated with `isValidUrlOwnershipPrefix` (exact http(s) origin
 * plus a path prefix, no wildcards), so one entry can never widen into a whole
 * domain.
 *
 * Shape: `[{ "prefix": "https://meet.example.com/sc/", "moduleId": "meet" }]`.
 */
export const URL_OWNERSHIP_KEY = 'platform.urlOwnership';

/**
 * Write each delivered push's **payload** to the log, not just its channel and
 * id (见 26 / 15).
 *
 * Off by default, and it has to be: the log pipeline persists to disk, while a
 * channel's `p` is whatever its backend chose to send — message bodies on `im`,
 * and on a channel nobody has written yet, anything at all. Recording it is a
 * decision about someone else's data, so it is a deployment's to make and not a
 * default to inherit.
 *
 * Turn it on for a联调 session, then turn it off. Everything that is *not* the
 * payload — channel, id, source, owning module — is logged either way, so this
 * is only needed when the routing is already known to work and the question has
 * become "what is actually in the frame".
 */
export const PUSH_PAYLOAD_LOG_KEY = 'platform.pushPayloadLog';

/**
 * Which module draws the block above the preferences-window search (见 21).
 *
 * `contributes.setting.header` is single-slot, and which module fills it is a
 * product decision, not something a module claims about itself — the same
 * reason as {@link PUSH_PROVIDER_KEY}. Empty draws no header.
 */
export const SETTINGS_HEADER_PROVIDER_KEY = 'workbench.settings.headerProvider';

export const PLATFORM_PRODUCT_CONFIG: Readonly<
  Record<string, ProductConfigContribution>
> = {
  [STARTUP_EDITOR_VIEW_KEY]: {
    type: 'string',
    description:
      'Editor view id opened on first workbench mount; empty leaves the editor blank.',
  },
  [RAIL_TOP_KEY]: {
    type: 'array',
    default: [],
    description:
      'Ordered launch item keys (`<moduleId>:<itemId>`) pinned to the top rail section by default.',
  },
  [RAIL_MIDDLE_KEY]: {
    type: 'array',
    default: [],
    description:
      'Ordered launch item keys pinned to the scrolling middle rail section by default.',
  },
  [RAIL_BOTTOM_KEY]: {
    type: 'array',
    default: [],
    description:
      'Ordered launch item keys pinned to the bottom rail section by default.',
  },
  [RAIL_LOCKED_KEY]: {
    type: 'array',
    default: [],
    description:
      'Launch item keys the user may not unpin. Deployment policy — the platform itself grants no entry this status, not even its own.',
  },
  [TRAY_ITEMS_KEY]: {
    type: 'array',
    default: [],
    description:
      'Ordered launch item keys (`<moduleId>:<itemId>`) the tray menu offers to open, below「打开 <appName>」.',
  },
  [UPDATE_FEED_URL_KEY]: {
    type: 'string',
    default: '',
    description:
      'Base URL of the release feed this build follows (serves the `<channel>.yml` update info plus installers). Empty disables self-update for the build.',
  },
  [UPDATE_CHECK_INTERVAL_KEY]: {
    type: 'number',
    default: DEFAULT_UPDATE_CHECK_INTERVAL_MINUTES,
    description:
      'Minutes between background update checks. 0 disables polling without disabling the manual check.',
  },
  [UPDATE_DOWNLOAD_PAGE_KEY]: {
    type: 'string',
    default: '',
    description:
      'Human-facing download page, offered when the runtime cannot install an update itself (unsigned macOS, Linux packages).',
  },
  [NETWORK_PROXY_KEY]: {
    type: 'string',
    default: 'system',
    description:
      'Egress policy for Node-side processes: "system" defers each origin to the OS proxy configuration (as the renderer already does), "direct" ignores it, or an absolute http(s) proxy URL pins one.',
  },
  [PUSH_PROVIDER_KEY]: {
    type: 'string',
    default: '',
    description:
      'Module id allowed to hand inbound platform pushes to the host for routing. Empty means this deployment has no push provider.',
  },
  [LINK_ALIASES_KEY]: {
    type: 'array',
    default: [],
    description:
      'Deployment-owned public deep-link prefix rewrites (`[{ from: "/public/", to: "/module/path/" }]`) applied once before structural module resolution.',
  },
  [URL_OWNERSHIP_KEY]: {
    type: 'array',
    default: [],
    description:
      'Deployment-owned external address ownership (`[{ prefix: "https://host/path/", moduleId }]`). Routes one of the deployment\'s own addresses to a module without editing that module\'s manifest; installed modules can also declare `contributes.urlOwnership` themselves.',
  },
  [PUSH_PAYLOAD_LOG_KEY]: {
    type: 'boolean',
    default: false,
    description:
      "Log each delivered push's payload, not just its channel and id. Off by default because logs persist to disk and a channel's payload is its backend's content (message bodies included). For debugging sessions only.",
  },
  [SETTINGS_HEADER_PROVIDER_KEY]: {
    type: 'string',
    default: '',
    description:
      'Module id whose `contributes.setting.header` view is drawn above the preferences-window search. Empty draws none.',
  },
};

/** Read a `workbench.rail.*` value as a string array, fail-soft. */
export function asKeyList(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}
